'use client';

import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@ui';
import { useClubSlug } from 'components/Clubs/hooks';
import { AnimatePresence, motion } from 'framer-motion';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Group, Panel, Separator } from 'react-resizable-panels';
import { AppSupportChatbotView } from './AppSupportChatbotView';
import { useAppSupportChatbot } from './useAppSupportChatbot';

type AppSupportChatSplitLayoutProps = {
    children: ReactNode;
    clubSlug?: string;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
};

export const AppSupportChatSplitLayout = ({ children, clubSlug: providedClubSlug, open: externalOpen, onOpenChange }: AppSupportChatSplitLayoutProps) => {
    const clubSlug = useClubSlug(providedClubSlug);
    const chatbot = useAppSupportChatbot(clubSlug);
    const [internalOpen, setInternalOpen] = useState(false);
    const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
    const setIsOpen = (v: boolean) => {
        if (onOpenChange) onOpenChange(v);
        else setInternalOpen(v);
    };

    // Track the resizable panel's position without storing it in state.
    // Direct DOM mutation means zero rerenders on resize.
    const chatPanelRef = useRef<HTMLDivElement>(null);
    const fixedPanelRef = useRef<HTMLDivElement>(null);
    const fixedSeparatorRef = useRef<HTMLDivElement>(null);

    const syncPanelPosition = useCallback(() => {
        const rect = chatPanelRef.current?.getBoundingClientRect();
        const fixed = fixedPanelRef.current;
        if (rect && fixed) {
            fixed.style.left = `${rect.left}px`;
            fixed.style.width = `${rect.width}px`;
        }

        const fixedSeparator = fixedSeparatorRef.current;
        if (rect && fixedSeparator) {
            fixedSeparator.style.left = `${Math.max(rect.left - 10, 0)}px`;
            fixedSeparator.style.top = '24px';
            fixedSeparator.style.height = 'calc(100vh - 48px)';
        }
    }, []);

    useEffect(() => {
        if (!isOpen) return;

        syncPanelPosition();

        const resizeObserver = new ResizeObserver(syncPanelPosition);
        if (chatPanelRef.current) resizeObserver.observe(chatPanelRef.current);
        window.addEventListener('resize', syncPanelPosition);
        window.addEventListener('scroll', syncPanelPosition, { passive: true });

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', syncPanelPosition);
            window.removeEventListener('scroll', syncPanelPosition);
        };
    }, [isOpen, syncPanelPosition]);

    if (!clubSlug || !chatbot.isMounted) return <>{children}</>;

    return (
        <div className="tw-relative tw-h-full">
            <div className="tw-flex tw-flex-col tw-gap-4 lg:tw-hidden">
                <div className="tw-min-w-0">{children}</div>
            </div>

            <div className="tw-hidden tw-h-full lg:tw-block">
                {/* No key prop — removing it prevents a full unmount/remount on open/close */}
                <Group orientation="horizontal" className="tw-h-full tw-w-full tw-overflow-hidden">
                    <Panel minSize={40}>
                        <div
                            className={`tw-h-full tw-min-w-0 tw-overflow-auto tw-pr-2 ${isOpen ? 'tw-mr-3' : 'tw-pr-4'}`}
                        >
                            {children}
                        </div>
                    </Panel>

                    {isOpen && (
                        <>
                            <Separator className="tw-w-1 tw-cursor-col-resize tw-rounded-full tw-bg-transparent tw-outline-none tw-ring-0 tw-ring-offset-0 focus:tw-outline-none focus:tw-ring-0 focus-visible:tw-outline-none focus-visible:tw-ring-0" />
                            <Panel defaultSize="30%" minSize="30%" maxSize="50%">
                                <div ref={chatPanelRef} className="tw-h-full">
                                    <div
                                        ref={fixedPanelRef}
                                        className="tw-fixed tw-bottom-4 tw-top-4 tw-flex tw-min-w-0 tw-overflow-hidden tw-pr-4"
                                    >
                                        <div className="tw-min-h-0 tw-min-w-0 tw-flex-1 tw-overflow-hidden">
                                            <AppSupportChatbotView
                                                {...chatbot}
                                                clubSlug={clubSlug}
                                                onClose={() => setIsOpen(false)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Panel>
                        </>
                    )}
                </Group>
                {isOpen && (
                    <div
                        ref={fixedSeparatorRef}
                        className="tw-pointer-events-none tw-fixed tw-z-40 tw-w-1 tw-rounded-full tw-bg-accents-3"
                    />
                )}
            </div>


            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="tw-fixed tw-inset-0 tw-z-50 tw-backdrop-blur-[2px] lg:tw-hidden"
                        onClick={() => setIsOpen(false)}
                    >
                        <div className="tw-flex tw-h-full tw-w-full tw-items-end tw-justify-end tw-p-3 sm:tw-p-4">
                            <motion.div
                                initial={{ opacity: 0, y: 24, scale: 0.98 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 20, scale: 0.98 }}
                                transition={{ type: 'spring', stiffness: 260, damping: 24 }}
                                className="tw-relative tw-h-[82vh] tw-w-full tw-overflow-hidden tw-rounded-[28px] tw-border tw-border-solid tw-border-accents-3 tw-bg-accents-0 tw-p-2 tw-shadow-2xl sm:tw-max-w-[440px]"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <Button
                                    size="sm"
                                    flat
                                    onPress={() => setIsOpen(false)}
                                    className="!tw-absolute !tw-right-4 !tw-top-4 !tw-z-10 !tw-h-10 !tw-rounded-full !tw-bg-white !tw-px-3 !tw-shadow-md"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                </Button>
                                <div className="tw-h-full tw-overflow-hidden tw-rounded-[22px] tw-bg-white tw-pt-10">
                                    <AppSupportChatbotView
                                        {...chatbot}
                                        clubSlug={clubSlug}
                                        onClose={() => setIsOpen(false)}
                                    />
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
